import { useMemo, memo } from "react";
import type { ToolUIPart } from "../../shared/types";
import { CardShell } from "./shared/CardShell";
import { InlineToolPanel } from "../InlineToolPanel";
import { parseToolOutput } from "./shared/utils";

const MAX_PREVIEW_ROWS = 8;

interface ParsedSheetData {
  ok: true;
  values?: string[][];
  range?: string;
  spreadsheet?: string;
  spreadsheetTitle?: string;
  sheetTitle?: string;
  webViewLink?: string;
  url?: string;
}

interface SheetViewCardProps {
  part: ToolUIPart;
}

export const SheetViewCard = memo(function SheetViewCard({ part }: SheetViewCardProps) {
  const isStreaming = part.state === "input-streaming";

  const result = useMemo(
    () => parseToolOutput<ParsedSheetData>(part.state === "output-available" ? part.output : undefined),
    [part.state, part.output],
  );

  if (part.state === "output-available" && result.kind === "parse-failed") {
    return <InlineToolPanel part={part} />;
  }

  const isError = result.kind === "error";
  const data = result.kind === "ok" ? result.data : null;

  const rows = data?.values ?? [];
  const hasData = rows.length > 0;
  const headerRow = hasData ? rows[0] : [];
  const dataRows = hasData ? rows.slice(1) : [];
  const previewRows = dataRows.slice(0, MAX_PREVIEW_ROWS);
  const overflow = Math.max(0, dataRows.length - MAX_PREVIEW_ROWS);

  const rangeLabel = data?.range ?? (data?.sheetTitle ? `Sheet: ${data.sheetTitle}` : "");
  const title = data?.spreadsheetTitle ?? data?.spreadsheet ?? "Spreadsheet";
  const link = data?.webViewLink ?? data?.url;

  const status = isStreaming ? "streaming" : isError ? "error" : "ready";
  const badge = isStreaming ? undefined : isError ? "error" : rangeLabel || undefined;

  return (
    <CardShell
      icon="⊞"
      title={title}
      badge={badge}
      accent={isError ? "error" : "sheets"}
      status={status}
    >
      {isStreaming && (
        <div className="tool-card__loading">Reading spreadsheet...</div>
      )}
      {isError && (
        <div className="tool-card__error-msg">{result.kind === "error" ? result.error : "Error"}</div>
      )}
      {!isStreaming && !isError && !hasData && (
        <div className="tool-card__empty">No data in range.</div>
      )}
      {!isStreaming && !isError && hasData && (
        <div className="tool-card__sheet-wrapper">
          <div className="tool-card__sheet-scroll" role="region" aria-label="Spreadsheet data">
            <table className="tool-card__sheet-table">
              <caption>{title}{rangeLabel ? ` — ${rangeLabel}` : ""}</caption>
              {headerRow.length > 0 && (
                <thead>
                  <tr>
                    {headerRow.map((cell, ci) => (
                      <th key={ci} className="tool-card__sheet-th" scope="col">
                        {cell}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {previewRows.map((row, ri) => (
                  <tr key={ri} className="tool-card__sheet-tr">
                    {headerRow.map((_, ci) => (
                      <td key={ci} className="tool-card__sheet-td">
                        {row[ci] ?? ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(overflow > 0 || link) && (
            <div className="tool-card__sheet-footer">
              {overflow > 0 && (
                <span className="tool-card__sheet-overflow">+{overflow} more row{overflow !== 1 ? "s" : ""}</span>
              )}
              {link && (
                <a
                  className="tool-card__sheet-link"
                  href={link}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={`Open ${title} in Sheets (opens in new tab)`}
                >
                  Open in Sheets ↗
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </CardShell>
  );
});
